import { Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { DatumOutputType, instanceOfCodeTabulationDatum, TabulationClassification, TabulationDatum } from '@rds/rds-base-ts';

export type DatumValueType = 'STRING' | 'NUMBER' | 'CODE';
/**
 * Determines whether the specified Datum is unique inside the given Datum array.
 * @param datum Datum to check for uniqueness
 * @param allDatum Datum array to search
 * @returns whether the datum is unique in the given array of datum
 */
export function isDatumUnique(datum: TabulationDatum, allDatum: TabulationDatum[]): boolean {
  return !allDatum.some((dt) => isMatchingDatum(dt, datum));
}

/**
 * Get the value from the datum. If the value is an object the optional `prop` can be specified.
 * @param d Datum to get the value
 * @param prop optional object property to return
 * @returns value of a datum
 */
export function getDatumValue<OT extends DatumOutputType = DatumOutputType>(
  d: TabulationDatum<OT> | null | undefined,
  variables: Array<Variable | VariableSummary | null>,
  classifications: { [id: string]: TabulationClassification },
  ...props: string[]
): DatumOutputType | null {
  if (!d) {
    return null;
  } else if (typeof d.o === 'object') {
    return (!!props && props.length ? props : ['codeValue'])
      .map((prop) => {
        if (prop === 'codeValue' && instanceOfCodeTabulationDatum(d)) {
          return d.o.cv;
        }
        if (prop === 'name' && instanceOfCodeTabulationDatum(d)) {
          const variable = variables.find((v) => v?.uri === d.v);
          return !variable?.classificationUri ? d.o.cv : classifications[variable.classificationUri].codes[d.o.cv];
        }
        return (d.o as { [key: string]: unknown })[prop];
      })
      .join(': ');
  }
  return d.o;
}

/**
 * Determines if the 'value' & 'variable' between 2 datum are the same.
 * @param d1 Datum
 * @param d2 Datum
 * @returns whether the datum are equal
 */
export function isMatchingDatum(d1: TabulationDatum, d2: TabulationDatum): boolean {
  // If either are null or the variables aren't the same
  if (!d1 || !d2 || d1.v !== d2.v) {
    return false;
  }
  // If they have code values, compare code values
  if (instanceOfCodeTabulationDatum(d1) && instanceOfCodeTabulationDatum(d2)) {
    return d1.o.cv === d2.o.cv;
  }
  return d1.o === d2.o;
}
